import type { Payment, Member } from '@/types';
import { formatDateDisplay } from '@/lib/dates';

export const MONTHLY_FEE = 100;
export const PLAN_MONTHS = 100;

export type MonthYear = {
  month: number; // 0 to 11
  year: number; // e.g. 2026
};

export const SHORT_MONTH_NAMES = [
  'jan', 'feb', 'mar', 'apr', 'may', 'jun',
  'jul', 'aug', 'sep', 'oct', 'nov', 'dec',
] as const;

export const DISPLAY_MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const;

export const getCurrentMonthYear = (): MonthYear => {
  const now = new Date();
  return {
    month: now.getMonth(),
    year: now.getFullYear(),
  };
};

export const formatMonthYearPayload = (my: MonthYear): string => {
  return `${SHORT_MONTH_NAMES[my.month]} ${my.year}`;
};

export const formatMonthYearDisplay = (my: MonthYear): string => {
  return `${DISPLAY_MONTH_NAMES[my.month]} ${my.year}`;
};

export const calculateDuePeriod = (start: MonthYear, end: MonthYear): number => {
  const months = (end.year - start.year) * 12 + (end.month - start.month) + 1;
  return Math.max(1, months);
};

export const addMonthsToMonthYear = (start: MonthYear, monthsToAdd: number): MonthYear => {
  const totalMonths = start.year * 12 + start.month + monthsToAdd;
  const year = Math.floor(totalMonths / 12);
  const month = ((totalMonths % 12) + 12) % 12;
  return { month, year };
};

export const compareMonthYear = (a: MonthYear, b: MonthYear): number => {
  if (a.year !== b.year) return a.year - b.year;
  return a.month - b.month;
};

export const isSameMonthYear = (a: MonthYear, b: MonthYear): boolean => {
  return a.month === b.month && a.year === b.year;
};

export const isMonthYearInRange = (target: MonthYear, start: MonthYear, end: MonthYear): boolean => {
  return compareMonthYear(target, start) >= 0 && compareMonthYear(target, end) <= 0;
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

export interface PaymentSummary {
  totalPaid: number;
  amountDue: number;
  monthsPaid: number;
  monthsRemaining: number;
  paymentProgress: number;
}

export interface PaymentMemberRow {
  member: Member;
  summary: PaymentSummary;
  isPaidThisMonth: boolean;
  lastPaymentDate: string | null;
  lastPaymentType: Payment['paymentType'] | null;
}

export const formatPaymentDate = (dateString: string | null | undefined): string =>
  formatDateDisplay(dateString);

export const formatPaymentType = (type: Payment['paymentType']): string => type;

export const getMonthYearFromDate = (dateString: string) => {
  const date = new Date(dateString);
  return {
    month: MONTH_NAMES[date.getMonth()],
    year: date.getFullYear(),
    monthIndex: date.getMonth() + 1,
  };
};

export const isSameCalendarMonth = (dateA: string, dateB: Date = new Date()) => {
  const a = new Date(dateA);
  return a.getMonth() === dateB.getMonth() && a.getFullYear() === dateB.getFullYear();
};

export const isPaidInMonth = (
  payments: Payment[],
  month: number,
  year: number
): boolean =>
  payments.some((payment) => {
    const paymentDate = new Date(payment.paymentDate);
    return paymentDate.getMonth() + 1 === month && paymentDate.getFullYear() === year;
  });

export const isPaidThisMonth = (payments: Payment[]): boolean => {
  const now = new Date();
  return isPaidInMonth(payments, now.getMonth() + 1, now.getFullYear());
};

export const hasPaymentForDate = (payments: Payment[], dateString: string): boolean => {
  const selected = new Date(dateString);
  return isPaidInMonth(payments, selected.getMonth() + 1, selected.getFullYear());
};

export const getPaymentSummary = (payments: Payment[]): PaymentSummary => {
  const monthsPaid = payments.length;
  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const monthsRemaining = Math.max(0, PLAN_MONTHS - monthsPaid);
  const amountDue = monthsRemaining * MONTHLY_FEE;
  const paymentProgress = Math.min(PLAN_MONTHS, monthsPaid);

  return {
    totalPaid,
    amountDue,
    monthsPaid,
    monthsRemaining,
    paymentProgress,
  };
};

export const getLastPayment = (payments: Payment[]): Payment | null => {
  if (payments.length === 0) return null;
  return [...payments].sort(
    (a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
  )[0];
};

export const sortPaymentsByDateDesc = (payments: Payment[]): Payment[] =>
  [...payments].sort(
    (a, b) => new Date(b.paymentDate).getTime() - new Date(a.paymentDate).getTime()
  );

export const buildPaymentMemberRow = (member: Member, payments: Payment[]): PaymentMemberRow => {
  const memberPayments = payments.filter((payment) => payment.memberId === member.id);
  const lastPayment = getLastPayment(memberPayments);

  return {
    member,
    summary: getPaymentSummary(memberPayments),
    isPaidThisMonth: isPaidThisMonth(memberPayments),
    lastPaymentDate: lastPayment?.paymentDate ?? null,
    lastPaymentType: lastPayment?.paymentType ?? null,
  };
};

export const formatCurrency = (amount: number): string =>
  `₹${amount.toLocaleString('en-IN')}`;

export const getCurrentMonthLabel = (): string =>
  new Date().toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
